import { useState } from 'react';
import { faqItems } from '@/mocks/faq';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  });

  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
            FAQ
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-charcoal mt-3 mb-4">
            Häufige Fragen
          </h2>
          <p className="font-sans text-base text-charcoal/60 leading-relaxed">
            Alles Wichtige rund um die kosmetische Zahnaufhellung in Köln.
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {faqItems.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className={`rounded-xl overflow-hidden transition-all duration-200 ${
                  isOpen ? 'ring-1 ring-rose' : 'ring-1 ring-blush'
                }`}
              >
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 bg-blush hover:bg-rose/10 transition-colors duration-200 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className="font-sans text-sm md:text-base font-semibold text-charcoal leading-snug">
                    {item.question}
                  </span>
                  <span className={`w-7 h-7 flex items-center justify-center flex-shrink-0 bg-white rounded-full transition-transform duration-300 ${isOpen ? 'rotate-45' : 'rotate-0'}`}>
                    <i className="ri-add-line text-rose text-lg" />
                  </span>
                </button>

                {/* Answer */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 py-5 bg-white">
                    <p className="font-sans text-sm text-charcoal/70 leading-[1.85]">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* More questions */}
        <div className="text-center mt-10">
          <p className="font-sans text-sm text-charcoal/60 mb-4">
            Noch Fragen? Schreib mir einfach!
          </p>
          <a
            href="https://wa.me/4922100000"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 bg-rose/10 hover:bg-rose/20 text-rose font-sans font-medium text-sm px-6 py-3 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base" />
            </span>
            Per WhatsApp fragen
          </a>
        </div>
      </div>
    </section>
  );
}
